import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCog,
  MapPin,
  DollarSign,
  BarChart3,
  Bell,
  FileText,
  Settings,
  Menu,
  X,
  LogOut,
  CreditCard,
  MessageSquare,
  CheckCircle,
  Route,
  LandPlot
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth, rtdb } from "@/lib/firebase";
import { getProfile } from "@/services/api";
import { ref, onValue, off } from "firebase/database";
import { cn } from "@/lib/utils";
import vruthiLogo from "@/assets/vruthi_logo.png";

export const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState({ firstName: "Admin", lastName: "", email: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const navigate = useNavigate();

  // Listen for open tickets count
  useEffect(() => {
    const ticketsRef = ref(rtdb, "tickets");
    const unsubscribe = onValue(ticketsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const count = Object.values(data).filter(t => t.status === 'open').length;
        setOpenTicketsCount(count);
      } else {
        setOpenTicketsCount(0);
      }
    });

    return () => off(ticketsRef);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const data = await getProfile();
          if (data && data.success && data.profile) {
            setProfile(data.profile);
          }
        } catch (error) {
          console.error("Error fetching profile for layout:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        navigate("/login");
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const navigation = [
    { name: "Overview", href: "/overview", icon: LayoutDashboard },
    { name: "Users", href: "/users", icon: Users },
    { name: "Map View", href: "/map", icon: MapPin },
    { name: "Local Map Assessment", href: "/local-map-assessment", icon: LandPlot },
    { name: "Route Design", href: "/route-design", icon: Route },
    { name: "Captains", href: "/workers", icon: UserCog },
    { name: "Pickups", href: "/pickups", icon: MapPin },
    { name: "Revenue", href: "/revenue", icon: DollarSign },
    { name: "Subscriptions", href: "/subscriptions", icon: CreditCard },
    { name: "Analytics", href: "/analytics", icon: BarChart3 },
    { 
      name: "Tickets", 
      href: "/tickets", 
      icon: MessageSquare,
      badge: openTicketsCount > 0 ? openTicketsCount : null
    },
    { name: "Notifications", href: "/notifications", icon: Bell },
    { name: "Manage App", href: "/content", icon: FileText },
    { name: "Settings", href: "/settings", icon: Settings },
    { name: "Daily Pickups", href: "/daily-coverage", icon: CheckCircle },
  ];

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-background via-background to-primary-light/20">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-card border-r border-border shadow-md">
        <div className="flex items-center gap-3 px-6 py-6 border-b border-border">
          <img
            src={vruthiLogo}
            alt="Vruthi logo"
            className="w-12 h-14 rounded-xl bg-white object-contain shadow-md"
          />
          <div>
            <h1 className="text-xl font-bold text-foreground">Vruthi</h1>
            <p className="text-xs text-muted-foreground">Admin Dashboard</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-smooth group ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-primary/10 hover:text-primary"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={`w-5 h-5 transition-smooth ${
                      isActive ? "" : "group-hover:scale-110"
                    }`}
                  />
                  <span className="flex-1">{item.name}</span>
                  {item.badge && (
                    <span className={cn(
                      "ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm",
                      isActive ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"
                    )}>
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 hover:bg-primary/10 transition-smooth"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {profile.firstName ? profile.firstName[0] : 'A'}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left flex-1">
                  <p className="text-sm font-medium text-foreground">{profile.firstName} {profile.lastName}</p>
                  <p className="text-xs text-muted-foreground">{profile.email}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-foreground/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border shadow-xl z-50">
            <div className="flex items-center justify-between px-6 py-6 border-b border-border">
              <div className="flex items-center gap-3">
                <img
                  src={vruthiLogo}
                  alt="Vruthi logo"
                  className="w-12 h-14 rounded-xl bg-white object-contain shadow-md"
                />
                <div>
                  <h1 className="text-xl font-bold text-foreground">Vruthi</h1>
                  <p className="text-xs text-muted-foreground">Admin Dashboard</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="hover:bg-primary/10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <nav className="px-3 py-4 space-y-1">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-smooth ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-primary/10 hover:text-primary"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1">{item.name}</span>
                      {item.badge && (
                        <span className={cn(
                          "ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm",
                          isActive ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"
                        )}>
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col h-full lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-card/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden hover:bg-primary/10"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="relative hover:bg-primary/10 transition-smooth"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-destructive rounded-full" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" className="hover:bg-primary/10">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        AD
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/settings")}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
